import { DateConfigTypes } from '@/types/analytics/analytics.types';
import { convertTimeSeriesForAggregation } from '@/utils/date-range.utils.js';
import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';
import { isAssistantMessage } from '@/ask-pendo/utils/message';
import { i18n_t as $t } from '@/utils/i18n';
import { v4 as uuid } from 'uuid';

export const dateRangeMapping = {
    TODAY: DateConfigTypes.Today,
    YESTERDAY: DateConfigTypes.Yesterday,
    LAST_7_DAYS: DateConfigTypes.Last7Days,
    LAST_30_DAYS: DateConfigTypes.Last30Days,
    LAST_90_DAYS: DateConfigTypes.Last90Days,
    LAST_180_DAYS: DateConfigTypes.Last180Days,
};

export const measureMapping = {
    TOTAL: 'Total events',
    AVERAGE: 'Visitor event average',
    AVERAGE_ACCOUNTS: 'Account event average',
    MEDIAN: 'Median events',
    NUM_VISITORS: 'Unique visitors',
    NUM_ACCOUNTS: 'Unique accounts',
    PERCENT_VISITORS: 'Percent of visitors',
    PERCENT_ACCOUNTS: 'Percent of accounts',
    ERROR_CLICKS: 'Error clicks',
    RAGE_CLICKS: 'Rage clicks',
    U_TURNS: 'U-turns',
};

/**
 * Convert a date range key to a valid timeSeries spec for aggregation
 *
 * @param {string} dateRangeKey - The date range string
 *   Valid values: 'TODAY', 'YESTERDAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_90_DAYS', 'LAST_180_DAYS'
 * @param {string} period - The period to use for the timeSeries spec
 *   Valid values: 'hourRange', 'dayRange', 'weekRange', 'monthRange', 'quarterRange',
 *                 'hourly', 'daily', 'weekly', 'monthly', 'quarterly'
 *
 * @returns {object} A timeSeries spec that can be used in aggregations
 */
export function convertDateRangeToTimeSeriesSpec (dateRangeKey, period = 'dayRange') {
    const dateConfigType = dateRangeMapping[dateRangeKey];

    if (!dateConfigType) {
        throw new Error(`Unknown dateRange key: ${dateRangeKey}`);
    }

    const config = {
        type: dateConfigType,
    };

    const fullTimeSeries = convertConfigToTimeSeries(config, true);
    const aggTimeSeries = convertTimeSeriesForAggregation(fullTimeSeries, true);
    aggTimeSeries.period = period;

    return aggTimeSeries;
}

/**
 * Get the icon name associated with a given artifact.
 *
 * @param artifact - An artifact object
 * @returns {string} An icon name
 */
export function getArtifactIcon (artifact) {
    switch (artifact.type) {
        case 'custom_highcharts':
            return 'bar-chart';

        case 'table':
            return 'table-2';

        case 'document':
            return 'file-text';

        case 'session_files':
            return 'folder';
    }

    return 'sparkles';
}

/**
 * Get the title associated with a given artifact.
 *
 * @param artifact - An artifact object
 * @returns {string} An artifact title
 */
export function getArtifactTitle (artifact) {
    const { name, type } = artifact;
    if (name) return name;

    switch (type) {
        case 'custom_highcharts':
            return $t('askPendo.artifactCard.Custom chart');
        case 'session_files':
            return $t('askPendo.artifactCard.Session files');
    }

    return $t('askPendo.artifactCard.Custom artifact');
}

/**
 * Retrieve a list of artifacts in the order that they appear in the conversation.
 *
 * @param conversation - The conversation object
 * @returns A list of objects with a `messageId` key and an `artifact` key mapping to
 *          the id of the message that refers to the artifact and the artifact
 *          object, respectively.
 */
export function getOrderedArtifacts (conversation) {
    const messages = conversation?.messages ?? [];
    const artifactById = conversation?.artifacts ?? {};

    const hasArtifacts = (message) => isAssistantMessage(message) && message.metadata?.artifact_ids?.length;

    return messages
        .filter(hasArtifacts)
        .map((message) =>
            message.metadata.artifact_ids.map((artifactId) => {
                return {
                    messageId: message.id,
                    artifact: artifactById[artifactId],
                };
            }),
        )
        .flat()
        .filter((data) => !!data.artifact);
}

/**
 * Transform artifact data into segment filter format
 * @param {Object} artifactData - The artifact data from the AI
 * @returns {Array} - Formatted filters array for segment editor
 */
export function transformArtifactToFilters (artifactData) {
    const { type, visitorIds = [], accountIds = [] } = artifactData;

    if (type === 'visitor' && visitorIds.length > 0) {
        // Create OR conditions for each visitor ID
        const orConditions = visitorIds.map((visitorId) => ({
            key: uuid(),
            group: 'auto',
            kind: 'visitor',
            field: 'visitor.auto.id',
            id: 'visitor_auto_id',
            type: 'visitor.auto.id',
            name: 'Visitor ID',
            schema: 'string',
            elementType: '',
            operator: '==',
            $valid: true,
            value: visitorId,
        }));

        return [
            {
                key: uuid(),
                or: orConditions,
            },
        ];
    }

    if (type === 'account' && accountIds.length > 0) {
        // Create OR conditions for each account ID
        const orConditions = accountIds.map((accountId) => ({
            key: uuid(),
            group: 'auto',
            kind: 'account',
            field: 'account.auto.id',
            id: 'account_auto_id',
            type: 'account.auto.id',
            name: 'Account ID',
            schema: 'string',
            elementType: '',
            operator: '==',
            $valid: true,
            value: accountId,
        }));

        return [
            {
                key: uuid(),
                or: orConditions,
            },
        ];
    }

    // Handle filters for countables (page, feature, track event)
    const filterConfig = {
        page: { id: 'page', name: 'Page', type: 'page', idField: 'pageId' },
        feature: { id: 'feature', name: 'Feature', type: 'feature', idField: 'featureId' },
        trackEvent: { id: 'trackType', name: 'Track Event', type: 'trackType', idField: 'trackTypeId' },
    };

    const config = filterConfig[type];
    const idValue = config && artifactData[config.idField];

    if (config && idValue) {
        const filter = {
            key: uuid(),
            id: config.id,
            name: config.name,
            type: config.type,
            kind: artifactData.kind || 'visitor',
            $valid: true,
            [config.idField]: idValue,
            // Always use counter schema for filters
            schema: 'counter',
            fact: artifactData.fact || 'used',
            used: artifactData.used || 'ever',
        };

        // For time-based filters (withinLast), add time/count/granularity/operator/value
        if (artifactData.used === 'withinLast') {
            filter.operator = artifactData.operator || '>=';
            filter.value = artifactData.value || 1;
            filter.time = artifactData.time || 'withinLast';
            filter.count = artifactData.count || 30;
            filter.granularity = artifactData.granularity || 'days';
        }

        return [filter];
    }

    // Return empty filters for other types or if no IDs
    return [];
}
